import { useAuth } from "@/hooks/useAuth";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PlayCircle, FileText, ExternalLink, Download } from "lucide-react";

const WALKTHROUGH_URL = "https://f.io/zFEqg32F";
const PDF_URL = "/Footage_Manager_User_Guide.pdf";

export default function Guide() {
  useAuth({ redirectOnUnauthenticated: true });

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      <main className="container py-10 max-w-3xl">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-foreground">Guide</h2>
          <p className="mt-1 text-muted-foreground">
            Everything you need to get started with Footage Manager.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          {/* Video walkthrough card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <PlayCircle className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">Walkthrough Video</CardTitle>
                  <CardDescription>
                    A full walkthrough of the upload workflow from patient setup to confirmation.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button
                asChild
                className="gap-2"
              >
                <a href={WALKTHROUGH_URL} target="_blank" rel="noopener noreferrer">
                  <PlayCircle className="w-4 h-4" />
                  Watch Walkthrough
                  <ExternalLink className="w-3.5 h-3.5 opacity-70" />
                </a>
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">
                Opens in Frame.io — no account required to view.
              </p>
            </CardContent>
          </Card>

          {/* PDF reference guide card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-base">Reference Guide (PDF)</CardTitle>
                  <CardDescription>
                    A printable step-by-step guide covering login, Google Drive setup, the Tag Library, and both upload workflows.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button asChild className="gap-2">
                <a href={PDF_URL} target="_blank" rel="noopener noreferrer" download="Footage_Manager_User_Guide.pdf">
                  <Download className="w-4 h-4" />
                  Download PDF
                </a>
              </Button>
              <p className="mt-3 text-xs text-muted-foreground">
                30-page illustrated guide — opens in a new tab or downloads directly depending on your browser.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Last updated: May 27, 2026
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
